import React from 'react';
import { PrinterIcon } from 'lucide-react';
import { Button, LinkButton } from '../components/Button';
import { GuideCta } from '../components/results/GuideCta';
import { PrimaryArchetypeCard } from '../components/results/PrimaryArchetypeCard';
import { ProfileBars } from '../components/results/ProfileBars';
import { ProfileCardActions } from '../components/results/ProfileCardActions';
import { siteConfig } from '../config/site';
import { ARCHETYPES } from '../data/archetypes';
import { goDeeper, profileNote } from '../data/introContent';
import type { ArchetypeResult } from '../types/assessment';
type ResultsProps = {
  profile: ArchetypeResult[];
};
export function Results({
  profile
}: ResultsProps) {
  const primary = ARCHETYPES[profile[0].key];
  const secondary = ARCHETYPES[profile[1].key];
  const match = ARCHETYPES[primary.matches];
  return <div className="mx-auto w-full max-w-reading px-6 pb-24" data-id="element-103" data-mp="x25w535v73ya">
      {/* 1 — Header */}
      <header className="print-block pb-8 pt-11 text-center" data-id="element-104" data-mp="s1ql4i9228jh">
        <p className="font-display text-[13px] uppercase tracking-[0.08em] text-ink-muted" data-id="element-105" data-mp="35bdcgdlvqzux">
          {siteConfig.brand.program}
        </p>
        <h1 className="mt-1.5 font-display text-[28px] font-bold leading-[1.15] text-ink sm:text-[38px]" data-id="element-106" data-mp="3a3oebm06kvq4">
          Your Relational Archetype Profile
        </h1>
      </header>

      {/* 2 — Primary archetype */}
      <PrimaryArchetypeCard result={profile[0]} data-id="element-107" data-mp="30a7k4gw4telx" />

      {/* 3 — Guide CTA, top */}
      <div className="mt-5" data-id="element-108" data-mp="3o2wdiewcg3ub">
        <GuideCta variant="feature" data-id="element-109" data-mp="3t17myoiq8cc0" />
      </div>

      {/* 4 — All eight percentages */}
      <section className="print-block mt-12" aria-labelledby="complete-profile-heading" data-id="element-110" data-mp="3syv7ci0fye24">
        <h2 id="complete-profile-heading" className="font-display text-xl font-semibold text-ink sm:text-2xl" data-id="element-111" data-mp="3e61m0fyhn3pj">
          Your Complete Relational Archetype Profile
        </h2>
        <p className="mt-1.5 text-sm text-ink-light" data-id="element-112" data-mp="22r4r4pfmhka9">
          Every archetype is present in you to some degree. These eight shares total 100% and show
          how your relational energy is distributed.
        </p>

        <div className="mt-6 rounded-xl border border-border bg-cream-light p-6 print-surface sm:p-7" data-id="element-113" data-mp="96baks9429tp">
          <ProfileBars profile={profile} data-id="element-114" data-mp="4et6hvbsjt4i" />
        </div>

        <div className="mt-5" data-id="element-115" data-mp="4di0rpyscbar">
          <ProfileCardActions profile={profile} data-id="element-116" data-mp="3vlxb6jkgr39r" />
        </div>

        <p className="mt-6 text-[13px] leading-relaxed text-ink-muted" data-id="element-117" data-mp="3vldut7agomxu">{profileNote}</p>
      </section>

      {/* 5 — Deeper readout */}
      <section className="print-block mt-12" aria-labelledby="deeper-heading" data-id="element-118" data-mp="3ml7hblerjmyo">
        <h2 id="deeper-heading" className="font-display text-xl font-semibold text-ink sm:text-2xl" data-id="element-119" data-mp="3mnbwizi448e9">
          What this pattern means
        </h2>

        <dl className="mt-5 space-y-4" data-id="element-120" data-mp="3cnteiolriwou">
          <div className="rounded-xl border border-border bg-cream-light p-6 print-surface" data-id="element-121" data-mp="3cow1wmjvmh00">
            <dt className="text-[10px] uppercase tracking-[0.16em] text-ink-muted" data-id="element-122" data-mp="1fsf5e87gd938">
              What sits underneath
            </dt>
            <dd className="mt-1.5 font-display text-[17px] italic leading-relaxed text-ink" data-id="element-123" data-mp="1ifh826fqns9a">
              {primary.vulnerable}
            </dd>
          </div>

          <div className="rounded-xl border border-border bg-cream-light p-6 print-surface" data-id="element-124" data-mp="1nbxw70mxu6md">
            <dt className="text-[10px] uppercase tracking-[0.16em] text-ink-muted" data-id="element-125" data-mp="1s5iu6soj70lk">Your strength</dt>
            <dd className="mt-1.5 font-display text-[17px] italic leading-relaxed text-ink" data-id="element-126" data-mp="1x32di70363kd">
              {primary.strength}
            </dd>
          </div>

          <div className="rounded-xl border border-border bg-cream-light p-6 print-surface" data-id="element-127" data-mp="14ed8vxztng3v">
            <dt className="text-[10px] uppercase tracking-[0.16em] text-ink-muted" data-id="element-128" data-mp="19dztishzbymp">
              Most often paired with
            </dt>
            <dd className="mt-1.5 flex items-center gap-2 text-[17px] text-ink" data-id="element-129" data-mp="1eenjrbydwbfv">
              <span aria-hidden="true" data-id="element-130" data-mp="2ksmuwlglcsrj">{match.emoji}</span> The {match.name}
            </dd>
          </div>
        </dl>

        <div className="mt-4 rounded-xl border border-border bg-cream-light p-6 print-surface sm:p-7" data-id="element-131" data-mp="2pqof59pr6y4l">
          <p className="text-[10px] uppercase tracking-[0.16em] text-ink-muted" data-id="element-132" data-mp="2nfxa2ah38f89">
            Secondary archetype
          </p>
          <h3 className="mt-1.5 font-display text-2xl font-semibold text-ink" data-id="element-133" data-mp="2xbqush4ynf14">
            <span aria-hidden="true" className="mr-2" data-id="element-134" data-mp="2x9nv15e0a7j7">
              {secondary.emoji}
            </span>
            The {secondary.name}
          </h3>
          <p className="mt-3 text-[15px] font-light leading-[1.78] text-ink-light" data-id="element-135" data-mp="36bne038kzlos">
            {secondary.desc}
          </p>
        </div>
      </section>

      {/* 6 — Save or print */}
      <section className="no-print mt-12 rounded-xl border border-border bg-cream-light px-6 py-7 text-center sm:px-8" data-id="element-136" data-mp="3g2rsz2y9b6jm">
        <h2 className="font-display text-lg font-semibold text-ink" data-id="element-137" data-mp="3b4q8cdl6mz61">Keep your full results</h2>
        <p className="mx-auto mt-1.5 max-w-md text-sm leading-relaxed text-ink-light" data-id="element-138" data-mp="3q1hagum8qozc">
          A clean copy of your primary archetype, its full readout, and all eight percentages.
        </p>
        <div className="mt-4 flex justify-center" data-id="element-139" data-mp="8j20hmb1z0l3">
          <Button variant="outline" onClick={() => window.print()} data-id="element-140" data-mp="cjlvokc2fu4l">
            <PrinterIcon size={17} aria-hidden="true" data-id="element-141" data-mp="cisxi7r6acy2" />
            Save or Print My Full Results
          </Button>
        </div>
      </section>

      {/* 7 — Go deeper + Guide CTA, bottom */}
      <section className="no-print mt-6 rounded-xl border border-teal/20 bg-teal-pale px-6 py-8 text-center sm:px-8" data-id="element-142" data-mp="3hcni149scud">
        <h2 className="font-display text-xl font-semibold text-ink" data-id="element-143" data-mp="3kxb03tych0aa">{goDeeper.heading}</h2>
        <p className="mx-auto mt-2 max-w-lg text-sm leading-relaxed text-ink-light" data-id="element-144" data-mp="3prf7gebivabw">
          {goDeeper.body}
        </p>
        <div className="mt-5 flex justify-center" data-id="element-145" data-mp="3gxtrwsrj9p1f">
          <LinkButton href={siteConfig.mainSiteUrl} target="_blank" rel="noopener noreferrer" data-id="element-146" data-mp="3gyloa5syfqsr">
            Explore with Dr. Conkright →
          </LinkButton>
        </div>
      </section>

      <div className="mt-6" data-id="element-147" data-mp="36yl0o7o1yoir">
        <GuideCta variant="compact" data-id="element-148" data-mp="36yusm0snr5r3" />
      </div>

      <footer className="print-block mt-10 text-center text-xs leading-relaxed text-ink-muted" data-id="element-149" data-mp="2nvj9o47fkuo7">
        Developed by{' '}
        <a href={siteConfig.mainSiteUrl} target="_blank" rel="noopener noreferrer" className="underline underline-offset-2" data-id="element-150" data-mp="2j39jcn2elsle">
          {siteConfig.brand.practitioner}
        </a>{' '}
        · {siteConfig.brand.theory} · {siteConfig.brand.practice}
      </footer>
    </div>;
}